/**
 * WB4.7 — medium-tier revision snapshot (server-persisted before-state).
 * Extends workbench-undo-v1 session model with durable restore payloads.
 */

import type { ObjectRef } from './workbench-focus';

import {
  clearNavigationRevisionSession,
  clearNavigationRevisionSnapshot,
  persistNavigationRevisionSnapshot,
  readNavigationRevisionSnapshot,
  resolveCrossRouteNavigationRestoreRevisionSnapshot,
} from './workbench-navigation-revision-session';
import { getWorkbenchRecoverabilityLadderEntry } from './workbench-recoverability-ladder';
import type { WorkbenchStagedWriteOptions } from './workbench-staged-write';
import type { WorkbenchTimelineEventInput } from './workbench-timeline-event';

export const WORKBENCH_REVISION_SNAPSHOT_DEFAULT_TTL_MS =
  getWorkbenchRecoverabilityLadderEntry('medium').revisionTtlMs ?? 24 * 60 * 60 * 1000;

export type WorkbenchRevisionRiskTier = 'medium' | 'high' | 'finance';

export type WorkbenchRevisionSnapshotKind =
  | 'operational_thread_advance'
  | 'entity_delete_before'
  | 'flow_start_context'
  | 'finance_reversal_pending'
  | 'bulk_state_change_before'
  | 'inline_field_change_before';

export interface BulkStateChangeItemSnapshot {
  objectId: string;
  businessDomain: string;
  state: unknown;
}

export interface BulkStateChangeBeforeRevisionPayload {
  kind: 'bulk_state_change_before';
  handlerRef: string;
  selectionCount: number;
  beforeStates: BulkStateChangeItemSnapshot[];
}

export interface InlineFieldChangeBeforeRevisionPayload {
  kind: 'inline_field_change_before';
  fieldKey: string;
  fieldLabel: string;
  previousValue: string;
  nextValue: string;
}

export interface OperationalThreadAdvanceRevisionPayload {
  kind: 'operational_thread_advance';
  stepId: string;
  beforeThread: unknown;
}

export interface EntityDeleteBeforeRevisionPayload {
  kind: 'entity_delete_before';
  entitySnapshot: unknown;
}

export interface FlowStartContextRevisionPayload {
  kind: 'flow_start_context';
  flowTemplateId: string;
  context?: unknown;
}

export interface FinanceReversalPendingRevisionPayload {
  kind: 'finance_reversal_pending';
  sourceVoucherId: string;
  streamType: 'reversal';
  streamTemplateId: string;
  requiresApproval: true;
  reversalRequestId?: string;
  reversalRequestStatus?: 'pending' | 'posted' | 'cancelled';
  compensatingStreamId?: string;
  compensatingVoucherId?: string;
}

export type WorkbenchRevisionSnapshotPayload =
  | OperationalThreadAdvanceRevisionPayload
  | EntityDeleteBeforeRevisionPayload
  | FlowStartContextRevisionPayload
  | FinanceReversalPendingRevisionPayload
  | BulkStateChangeBeforeRevisionPayload
  | InlineFieldChangeBeforeRevisionPayload;

export interface WorkbenchRevisionSnapshotRecord {
  id: string;
  tenantId: string;
  objectId: string;
  businessDomain: string;
  objectLabel?: string;
  actionId: string;
  actionLabel: string;
  handlerRef: string;
  riskTier: WorkbenchRevisionRiskTier;
  payload: WorkbenchRevisionSnapshotPayload;
  createdAt: string;
  expiresAt: string;
  restoredAt?: string;
}

export interface SaveWorkbenchRevisionSnapshotInput {
  tenantId: string;
  objectRef: ObjectRef;
  actionId: string;
  actionLabel: string;
  handlerRef: string;
  payload: WorkbenchRevisionSnapshotPayload;
  riskTier?: WorkbenchRevisionRiskTier;
  ttlMs?: number;
}

/** Audit/timeline `change` block from revision before-state — no separate snapshot fetch. */
export function buildRevisionAuditChange(
  payload: WorkbenchRevisionSnapshotPayload,
  objectRef?: Pick<ObjectRef, 'id' | 'label'>
): {
  fieldKey?: string;
  before?: unknown;
  after?: unknown;
  diffSummary: string;
} {
  const objectLabel = objectRef?.label?.trim() || objectRef?.id || 'object';
  switch (payload.kind) {
    case 'operational_thread_advance':
      return {
        fieldKey: 'operational_thread',
        before: payload.beforeThread,
        diffSummary: `단계 ${payload.stepId} 진행 (Operational Thread)`,
      };
    case 'bulk_state_change_before':
      return {
        fieldKey: 'bulk_state',
        before: payload.beforeStates,
        diffSummary: `일괄 변경 ${payload.selectionCount}건 · ${payload.handlerRef}`,
      };
    case 'entity_delete_before':
      return {
        fieldKey: 'entity',
        before: payload.entitySnapshot,
        after: null,
        diffSummary: `${objectLabel} 삭제`,
      };
    case 'flow_start_context':
      return {
        fieldKey: 'flow',
        before: payload.context ?? null,
        diffSummary: `Flow 시작 · ${payload.flowTemplateId}`,
      };
    case 'finance_reversal_pending':
      return {
        fieldKey: 'finance_reversal',
        before: payload.sourceVoucherId,
        diffSummary: `역분개 ${payload.reversalRequestStatus ?? 'pending'} · ${payload.sourceVoucherId}`,
      };
    case 'inline_field_change_before':
      return {
        fieldKey: payload.fieldKey,
        before: payload.previousValue,
        after: payload.nextValue,
        diffSummary: `${payload.fieldLabel} 수정 · ${payload.previousValue} → ${payload.nextValue}`,
      };
    default: {
      const _exhaustive: never = payload;
      return _exhaustive;
    }
  }
}

function createRevisionSnapshotId(): string {
  return `wbx-rev-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function scopeKey(tenantId: string, objectId: string): string {
  return `${tenantId}:${objectId}`;
}

const snapshotsByScope = new Map<string, WorkbenchRevisionSnapshotRecord>();
const snapshotsById = new Map<string, WorkbenchRevisionSnapshotRecord>();
let sessionSnapshot: WorkbenchRevisionSnapshotRecord | null = null;

export function isWorkbenchRevisionSnapshotActive(
  snapshot: Pick<WorkbenchRevisionSnapshotRecord, 'expiresAt' | 'restoredAt'>
): boolean {
  if (snapshot.restoredAt) {
    return false;
  }
  const expiresAt = Date.parse(snapshot.expiresAt);
  if (Number.isNaN(expiresAt)) {
    return false;
  }
  return Date.now() < expiresAt;
}

function pruneInactiveSnapshot(snapshot: WorkbenchRevisionSnapshotRecord): void {
  if (isWorkbenchRevisionSnapshotActive(snapshot)) {
    return;
  }
  snapshotsById.delete(snapshot.id);
  const key = scopeKey(snapshot.tenantId, snapshot.objectId);
  if (snapshotsByScope.get(key)?.id === snapshot.id) {
    snapshotsByScope.delete(key);
  }
  if (sessionSnapshot?.id === snapshot.id) {
    sessionSnapshot = null;
  }
}

export function saveWorkbenchRevisionSnapshot(
  input: SaveWorkbenchRevisionSnapshotInput,
  options?: WorkbenchStagedWriteOptions
): WorkbenchRevisionSnapshotRecord {
  const key = scopeKey(input.tenantId, input.objectRef.id);
  const previousScope = snapshotsByScope.get(key) ?? null;
  const previousSession = sessionSnapshot;

  const createdAt = new Date().toISOString();
  const ttlMs = input.ttlMs ?? WORKBENCH_REVISION_SNAPSHOT_DEFAULT_TTL_MS;
  const record: WorkbenchRevisionSnapshotRecord = {
    id: createRevisionSnapshotId(),
    tenantId: input.tenantId,
    objectId: input.objectRef.id,
    businessDomain: input.objectRef.businessDomain,
    ...(input.objectRef.label ? { objectLabel: input.objectRef.label } : {}),
    actionId: input.actionId,
    actionLabel: input.actionLabel,
    handlerRef: input.handlerRef,
    riskTier: input.riskTier ?? 'medium',
    payload: input.payload,
    createdAt,
    expiresAt: new Date(Date.now() + ttlMs).toISOString(),
  };

  if (previousScope) {
    snapshotsById.delete(previousScope.id);
  }

  snapshotsByScope.set(key, record);
  snapshotsById.set(record.id, record);
  sessionSnapshot = record;
  persistNavigationRevisionSnapshot(record);

  options?.tx?.stageMemoryRollback(() => {
    snapshotsById.delete(record.id);
    if (previousScope) {
      snapshotsByScope.set(key, previousScope);
      snapshotsById.set(previousScope.id, previousScope);
    } else {
      snapshotsByScope.delete(key);
    }
    sessionSnapshot = previousSession;
  });

  return record;
}

export function getWorkbenchRevisionSnapshotById(
  snapshotId: string
): WorkbenchRevisionSnapshotRecord | null {
  const snapshot = snapshotsById.get(snapshotId);
  if (!snapshot) {
    return null;
  }
  if (!isWorkbenchRevisionSnapshotActive(snapshot)) {
    pruneInactiveSnapshot(snapshot);
    return null;
  }
  return snapshot;
}

export function getLatestWorkbenchRevisionSnapshot(input: {
  tenantId: string;
  objectId: string;
}): WorkbenchRevisionSnapshotRecord | null {
  const fromServer = snapshotsByScope.get(scopeKey(input.tenantId, input.objectId));
  if (fromServer) {
    if (isWorkbenchRevisionSnapshotActive(fromServer)) {
      sessionSnapshot = fromServer;
      return fromServer;
    }
    pruneInactiveSnapshot(fromServer);
  }

  if (
    sessionSnapshot &&
    sessionSnapshot.tenantId === input.tenantId &&
    sessionSnapshot.objectId === input.objectId &&
    isWorkbenchRevisionSnapshotActive(sessionSnapshot)
  ) {
    return sessionSnapshot;
  }

  return readNavigationRevisionSnapshot(input);
}

export function markWorkbenchRevisionSnapshotRestored(
  snapshotId: string,
  options?: WorkbenchStagedWriteOptions
): WorkbenchRevisionSnapshotRecord | null {
  const snapshot = snapshotsById.get(snapshotId) ?? null;
  if (!snapshot || !isWorkbenchRevisionSnapshotActive(snapshot)) {
    return null;
  }
  const previousScope =
    snapshotsByScope.get(scopeKey(snapshot.tenantId, snapshot.objectId)) ?? null;
  const previousSession = sessionSnapshot;
  const restored: WorkbenchRevisionSnapshotRecord = {
    ...snapshot,
    restoredAt: new Date().toISOString(),
  };
  snapshotsById.set(snapshot.id, restored);
  snapshotsByScope.set(scopeKey(snapshot.tenantId, snapshot.objectId), restored);
  sessionSnapshot = null;
  clearNavigationRevisionSnapshot({
    tenantId: snapshot.tenantId,
    objectId: snapshot.objectId,
  });

  options?.tx?.stageMemoryRollback(() => {
    snapshotsById.set(snapshot.id, snapshot);
    if (previousScope) {
      snapshotsByScope.set(scopeKey(snapshot.tenantId, snapshot.objectId), previousScope);
    }
    sessionSnapshot = previousSession;
  });

  return restored;
}

export function clearWorkbenchRevisionSnapshotSession(): void {
  sessionSnapshot = null;
}

/**
 * Session-scoped navigation restore — returns the active snapshot for the object
 * after the user navigates away and returns within the TTL window.
 */
export function resolveNavigationRestoreRevisionSnapshot(input: {
  tenantId: string;
  objectId: string;
}): WorkbenchRevisionSnapshotRecord | null {
  return (
    getLatestWorkbenchRevisionSnapshot(input) ??
    resolveCrossRouteNavigationRestoreRevisionSnapshot(input)
  );
}

function resolveRevisionTimelineObjectRef(
  revision: Pick<WorkbenchRevisionSnapshotRecord, 'businessDomain'>
): {
  domain: string;
  objectType: string;
} {
  const businessDomain = revision.businessDomain;
  if (businessDomain === 'finance_state' || businessDomain === 'finance_flow') {
    return { domain: 'finance', objectType: businessDomain };
  }
  if (businessDomain.endsWith('_item')) {
    return { domain: 'item', objectType: businessDomain };
  }
  if (businessDomain.endsWith('_entity')) {
    return { domain: 'entity', objectType: businessDomain };
  }
  return { domain: businessDomain, objectType: businessDomain };
}

/** SSOT input for revision_restored timeline append (server + client best-effort). */
export function buildRevisionRestoredTimelineInput(
  revision: Pick<
    WorkbenchRevisionSnapshotRecord,
    | 'tenantId'
    | 'objectId'
    | 'businessDomain'
    | 'actionId'
    | 'actionLabel'
    | 'handlerRef'
    | 'riskTier'
    | 'id'
    | 'restoredAt'
  >,
  restoredBy?: string
): WorkbenchTimelineEventInput {
  const { domain, objectType } = resolveRevisionTimelineObjectRef(revision);
  return {
    tenantId: revision.tenantId,
    domain,
    objectType,
    objectId: revision.objectId,
    type: 'revision_restored',
    title: `복원: ${revision.actionLabel}`,
    description: revision.handlerRef,
    actor: {
      userId: restoredBy?.trim() || 'system',
    },
    action: {
      actionId: revision.actionId,
      actionLabel: revision.actionLabel,
      riskTier: revision.riskTier,
      surface: 'inspector',
    },
    related: {
      revisionId: revision.id,
    },
    recoverability: {
      mode: 'revision_restore',
      available: false,
      reason: 'revision_restored',
    },
    severity: 'info',
    ...(revision.restoredAt ? { createdAt: revision.restoredAt } : {}),
  };
}

/** Test-only reset */
export function clearWorkbenchRevisionSnapshotStore(): void {
  snapshotsByScope.clear();
  snapshotsById.clear();
  sessionSnapshot = null;
  clearNavigationRevisionSession();
}
